<template>
  <!-- Nessuna UI propria: l'avviso lo rende Alert.vue, montato una volta in App.vue.
       Il nodo sta qui solo perché un template deve avere una radice: non finisce nel DOM. -->
  <span v-if="false" />
</template>

<script setup lang="ts">
import { onMounted, watch } from "vue";
import { useSocket } from "@/composables/useSocket";
import { AlertTypes, useCommonAlert } from "@/store/commonAlert";
import { useNotification } from "@/store/notification";
import { useSocketEvents } from "@/store/socketEvents";
import type { NotificationSocketPayloadType } from "@/types/notification";
import type { ReportJobSocketPayloadType } from "@/types/report";
import { SOCKET_EVENT_SOURCE } from "@/types/socket";

/**
 * Unica connessione socket dell'applicazione: ascolta gli eventi di service-socket e li
 * ripubblica sul bus (store socketEvents), così chiunque può reagire senza aprire un canale proprio.
 *
 * Gli eventi arrivano sulla room che service-socket assegna da sé all'handshake (user:<sub>),
 * autenticato col cookie hd-context: non c'è nessuna room da chiedere e basta una connessione
 * per utente.
 */

const alertStore = useCommonAlert();
const socketEvents = useSocketEvents();
const notificationStore = useNotification();

// scope attivo: il canale si chiude da sé allo smontaggio del componente
const channel = useSocket();

// Dopo una riconnessione il server non rimanda gli eventi persi: si chiede ai consumatori delle
// notifiche di riallinearsi dal backend. Il primo collegamento non conta, lì carica il login.
let hasConnectedOnce = false;
watch(channel.isConnected, (connected) => {
  // a socket giù lo store torna al polling di fallback, a socket su rallenta al riallineamento
  notificationStore.setSocketConnected(connected);
  if (!connected) return;
  if (!hasConnectedOnce) {
    hasConnectedOnce = true;
    return;
  }
  socketEvents.publish<NotificationSocketPayloadType>(SOCKET_EVENT_SOURCE.Notification, {
    action: "resync",
  });
  notificationStore.init().catch((error) =>
    console.error("[AppSocketListener] notification resync failed", error)
  );
});

onMounted(() => {
  /**
   * Job report concluso: avviso generico ovunque si trovi l'utente — i dettagli stanno nello
   * storico della pagina Report — che non blocca l'operatività e si chiude da solo.
   */
  channel.on<ReportJobSocketPayloadType>(SOCKET_EVENT_SOURCE.ReportJob, (payload) => {
    // ripubblicato sul bus: chi è aperto sulla pagina Report ricarica lo storico senza
    // aprire una connessione propria
    socketEvents.publish(SOCKET_EVENT_SOURCE.ReportJob, payload);

    const isReady = payload.status === "Ready";

    alertStore.showAlert({
      title: isReady
        ? "Report pronto: lo trovi nello storico della pagina Report"
        : "Generazione del report non riuscita",
      type: isReady ? AlertTypes.SUCCESS : AlertTypes.ERROR,
    });
  });

  /**
   * Notifiche: il payload è solo un segnale (la forma varia fra i producer), quindi si rilegge
   * la lista intera e i conteggi della campanella seguono. Ripubblicato sul bus perché il centro
   * notifiche ricarichi la propria pagina.
   */
  channel.on<NotificationSocketPayloadType>(SOCKET_EVENT_SOURCE.Notification, async (payload) => {
    socketEvents.publish(SOCKET_EVENT_SOURCE.Notification, payload);

    try {
      await notificationStore.init();
    } catch (error) {
      console.error("[AppSocketListener] notification reload failed", error);
      return;
    }    
  });

  channel.connect();
});
</script>
